import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Button,
  useToast,
  VStack,
  HStack,
  Badge,
  Switch,
  Tooltip,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon, EditIcon, SearchIcon } from "@chakra-ui/icons";
import { MdLocalOffer } from "react-icons/md";
import * as React from "react";
import { resolveBusinessPanelMediaUrl } from "../../../../utils/cloudinaryDeliveryUrl";
import {
  createColumnHelper,
  flexRender,
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import AddProductModal from "./AddProductModal";
import AddDiscountProductModal from "./AddDiscountProductModal";
import Card from "../../../../components/card/Card";
import { menuService } from "../../../../services/menuService";
import { pricingService } from "../../../../services/pricingService";
import PremiumUpgradeModal from "../../../../components/modals/PremiumUpgradeModal";
import { AddIcon } from "@chakra-ui/icons";

const columnHelper = createColumnHelper();

// STANDARD plan için maksimum ürün sayısı
const STANDARD_PRODUCT_LIMIT = 40;

export default function ProductTable({
  tableData = [],
  categories = [],
  setTableData,
  onRefresh  // onRefresh prop'unu kullanacağız
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [editingProduct, setEditingProduct] = React.useState(null);
  const [isDiscountModalOpen, setIsDiscountModalOpen] = React.useState(false);
  const [productForDiscount, setProductForDiscount] = React.useState(null);
  const [userPlan, setUserPlan] = React.useState('STANDARD');
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = React.useState(false);
  const [productToDelete, setProductToDelete] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const { isOpen: isDeleteDialogOpen, onOpen: onDeleteDialogOpen, onClose: onDeleteDialogClose } = useDisclosure();
  const cancelRef = React.useRef();
  const toast = useToast();
  
  // Plan bilgisini çek
  React.useEffect(() => {
    const fetchPlan = async () => {
      try {
        const pricingInfo = await pricingService.getPricingInfo();
        if (pricingInfo?.success && pricingInfo?.data?.business?.plan) {
          setUserPlan(pricingInfo.data.business.plan);
        }
      } catch (error) {
        console.error('Plan bilgisi alınamadı:', error);
        const business = JSON.parse(localStorage.getItem('business') || '{}');
        if (business.plan) {
          setUserPlan(business.plan);
        }
      }
    };
    fetchPlan();
  }, []);

  const handleOpenModal = () => {
    // STANDARD plan için ürün limiti kontrolü (sadece yeni ürün eklerken)
    if (!editingProduct && userPlan === 'STANDARD' && tableData.length >= STANDARD_PRODUCT_LIMIT) {
      toast({
        title: 'Ürün Limiti',
        description: `STANDARD plan için maksimum ${STANDARD_PRODUCT_LIMIT} ürün eklenebilir. Daha fazla ürün eklemek için PREMIUM plana yükseltin.`,
        status: 'warning',
        duration: 5000,
      });
      setIsUpgradeModalOpen(true);
      return;
    }
    setIsOpen(true);
  };
  const handleCloseModal = () => {
    setIsOpen(false);
    setEditingProduct(null);
  };
  const handleEditClick = (product) => {
    setEditingProduct(product);
    setIsOpen(true);
  };
  
  const handleDiscountClick = (product) => {
    setProductForDiscount(product);
    setIsDiscountModalOpen(true);
  };
  
  const handleCloseDiscountModal = () => {
    setIsDiscountModalOpen(false);
    setProductForDiscount(null);
  };
  
  const handleDiscountUpdate = async (discountData) => {
    try {
      // productForDiscount varsa onu kullan, yoksa discountData'dan productId al
      const productId = productForDiscount?._id || discountData.productId;
      
      if (!productId) {
        toast({
          title: 'Hata',
          description: 'Ürün seçilmedi',
          status: 'error',
          duration: 3000,
        });
        return;
      }
      
      const response = await menuService.updateProductDiscount(productId, discountData);
      
      // Verileri manuel güncelle (onRefresh yerine)
      if (response && response.data) {
        const updatedProduct = response.data;
        setTableData(prevData => 
          prevData.map(product => 
            product._id === productId 
              ? { ...product, ...updatedProduct }
              : product
          )
        );
      } else {
        // Eğer response'da data yoksa, onRefresh kullan
        await onRefresh();
      }
      
      handleCloseDiscountModal();
      toast({
        title: 'Başarılı',
        description: 'Ürün indirimi başarıyla güncellendi',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Discount update error:', error);
      toast({
        title: 'Hata',
        description: error.response?.data?.message || 'İndirim güncellenirken bir hata oluştu',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleAddProduct = async (formData) => {
    try {
      // Edit modunda değilse STANDARD plan için ürün limiti kontrolü
      if (!editingProduct && userPlan === 'STANDARD' && tableData.length >= STANDARD_PRODUCT_LIMIT) {
        toast({
          title: 'Ürün Limiti',
          description: `STANDARD plan için maksimum ${STANDARD_PRODUCT_LIMIT} ürün eklenebilir. Daha fazla ürün eklemek için PREMIUM plana yükseltin.`,
          status: 'warning',
          duration: 5000,
        });
        setIsUpgradeModalOpen(true);
        handleCloseModal();
        return;
      }
      
      const restaurantId = localStorage.getItem('restaurant');
      formData.append('restaurantId', restaurantId);
      
      let response;
      if (editingProduct) {
        // Edit modunda
        response = await menuService.updateProduct(editingProduct._id, formData);
      } else {
        // Yeni ürün ekleme
        response = await menuService.addProduct(formData);
      }
      
      if (response.success) {
        handleCloseModal();
        await onRefresh(); // Tüm verileri yeniden çek
        toast({
          title: 'Başarılı',
          description: editingProduct ? 'Ürün başarıyla güncellendi' : 'Ürün başarıyla eklendi',
          status: 'success',
          duration: 3000,
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || (editingProduct ? 'Ürün güncellenirken bir hata oluştu' : 'Ürün eklenirken bir hata oluştu');
      
      // Eğer backend'den limit hatası gelirse modal aç
      if (errorMessage.includes('STANDARD plan') || errorMessage.includes('maksimum')) {
        setIsUpgradeModalOpen(true);
        handleCloseModal();
      }
      
      toast({
        title: 'Hata',
        description: errorMessage,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleDeleteClick = (productId) => {
    setProductToDelete(productId);
    onDeleteDialogOpen();
  };

  const handleDeleteProduct = async () => {
    if (!productToDelete) return;
    
    try {
      await menuService.deleteProduct(productToDelete);
      setTableData(prevData => 
        prevData.filter(product => product._id !== productToDelete)
      );
      await onRefresh(); // Tüm verileri yeniden çek
      toast({
        title: 'Başarılı',
        description: 'Ürün başarıyla silindi',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Delete product error:', error);
      toast({
        title: 'Hata',
        description: 'Ürün silinirken bir hata oluştu',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setProductToDelete(null);
      onDeleteDialogClose();
    }
  };

  const handleStatusChange = async (productId, isActive) => {
    try {
      await menuService.updateProductStatus(productId, isActive);
      setTableData(prevData => 
        prevData.map(product => 
          product._id === productId 
            ? { ...product, isActive } 
            : product
        )
      );
      toast({
        title: "Başarılı",
        description: `Ürün ${isActive ? 'aktif' : 'pasif'} duruma getirildi`,
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Hata",
        description: "Durum güncellenirken bir hata oluştu",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleFeaturedChange = async (productId, isFeatured) => {
    try {
      await menuService.updateProductFeatured(productId, isFeatured);
      setTableData(prevData => 
        prevData.map(product => 
          product._id === productId 
            ? { ...product, isFeatured } 
            : product
        )
      );
      toast({
        title: "Başarılı",
        description: `Ürün ${isFeatured ? 'öne çıkan' : 'normal'} olarak işaretlendi`,
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Hata",
        description: "Öne çıkan durumu güncellenirken bir hata oluştu",
        status: "error",
        duration: 3000,
      });
    }
  };

  const columns = [
    columnHelper.accessor("name", {
      header: "Ürün Adı",
      cell: ({ getValue }) => (
        <Text fontWeight="medium">{getValue()}</Text>
      ),
    }),
    columnHelper.accessor("price", {
      header: "Ürün Fiyatı",
      cell: ({ row }) => {
        const product = row.original;
        const hasDiscount = product.isOnSale && product.discountPrice;
        const price = Number(product.price) || 0;
        const discountPrice = Number(product.discountPrice) || 0;
        
        return (
          <VStack align="start" spacing={0}>
            {hasDiscount ? (
              <>
                <Text as="s" color="gray.400" fontSize="sm">{price.toFixed(2)} ₺</Text>
                <Text color="green.600" fontWeight="bold">{discountPrice.toFixed(2)} ₺</Text>
                {product.discountType === 'percentage' && product.discountValue && (
                  <Badge colorScheme="green" fontSize={{ base: "2xs", md: "xs" }}>%{product.discountValue} İndirim</Badge>
                )}
                {product.discountType === 'fixed' && product.discountValue && (
                  <Badge colorScheme="green" fontSize={{ base: "2xs", md: "xs" }}>{product.discountValue}₺ İndirim</Badge>
                )}
              </>
            ) : (
              <Text color="#e45858" fontWeight="medium">{price.toFixed(2)} ₺</Text>
            )}
          </VStack>
        );
      },
    }),
    columnHelper.accessor("categoryId", {
      header: "Kategori",
      cell: ({ getValue }) => {
        const category = categories.find(c => c._id === getValue());
        return <Text>{category?.name || '-'}</Text>;
      },
    }),
    columnHelper.accessor("ingredients", {
      header: "İçindekiler",
      cell: ({ getValue }) => {
        const ingredients = getValue() || [];
        const displayCount = 2;
        const displayedIngredients = ingredients.slice(0, displayCount);
        const remainingCount = ingredients.length - displayCount;

        return (
          <VStack align="start" spacing={1}>
            {displayedIngredients.map((ingredient, index) => (
              <Text key={index} fontSize="sm" color="gray.600">
                • {ingredient.name}
              </Text>
            ))}
            {remainingCount > 0 && (
              <Tooltip
                label={
                  <VStack align="start" spacing={1} p={2}>
                    {ingredients.slice(displayCount).map((ingredient, index) => (
                      <Text key={index} fontSize="sm">
                        • {ingredient.name}
                      </Text>
                    ))}
                  </VStack>
                }
                hasArrow
                placement="bottom"
                bg="white"
                color="gray.800"
                openDelay={0}
                closeOnClick={true}
              >
                <Text fontSize="sm" color="gray.500" cursor="pointer">
                  +{remainingCount} daha
                </Text>
              </Tooltip>
            )}
          </VStack>
        );
      },
    }),
    columnHelper.accessor("preparationTime", {
      header: "Hazırlanış Süresi",
      cell: ({ row }) => {
        const product = row.original;
        if (product.preparationTimeMin && product.preparationTimeMax) {
          return `${product.preparationTimeMin}-${product.preparationTimeMax} dk`;
        }
        return '-';
      },
    }),
    columnHelper.accessor('image', {
      header: 'Görsel',
      cell: ({ row }) => {
        const product = row.original;
        const apiUrl =
          import.meta.env.REACT_APP_API_URL ||
          process.env.REACT_APP_API_URL ||
          'http://localhost:5001';
        const imageUrl = product.image
          ? resolveBusinessPanelMediaUrl(product.image, apiUrl, 'square500')
          : '/placeholder.png';

        return (
          <img
            src={imageUrl}
            alt={product.name}
            style={{ 
              width: '50px', 
              height: '50px', 
              objectFit: 'cover',
              borderRadius: '4px'
            }}
            onError={(e) => {
              e.target.src = '/placeholder.png';
            }}
            crossOrigin="anonymous"
          />
        );
      },
    }),
    columnHelper.accessor("options", {
      header: "Seçenekler",
      cell: ({ getValue }) => {
        const options = getValue() || [];
        if (options.length === 0) return "-";

        const displayCount = 1;
        const displayedOptions = options.slice(0, displayCount);
        const remainingCount = options.length - displayCount;

        return (
          <VStack align="start" spacing={1}>
            {displayedOptions.map((option, index) => {
              const choicesText = option.choices?.map(choice => {
                const price = Number(choice.price) || 0;
                const priceText = price > 0 ? ` (+${price.toFixed(2)}₺)` : '';
                return `${choice.name}${priceText}`;
              }).join(', ') || '';
              return (
                <Text key={index} fontSize="sm" color="blue.600" fontWeight="500">
                  • {option.name}: {choicesText}
                </Text>
              );
            })}
            {remainingCount > 0 && (
              <Tooltip
                label={
                  <VStack align="start" spacing={2} p={2} maxH="300px" overflowY="auto" bg="white" borderRadius="md">
                    {options.slice(displayCount).map((option, index) => {
                      const choicesText = option.choices?.map(choice => {
                        const price = Number(choice.price) || 0;
                        const priceText = price > 0 ? ` (+${price.toFixed(2)}₺)` : '';
                        return `${choice.name}${priceText}`;
                      }).join(', ') || '';
                      return (
                        <Box key={index}>
                          <Text fontSize="sm" color="blue.600" fontWeight="500">
                            {option.name}:
                          </Text>
                          <Text fontSize="sm" color="black" pl={2}>
                            {choicesText}
                          </Text>
                        </Box>
                      );
                    })}
                  </VStack>
                }
                hasArrow
                placement="bottom"
                bg="blue.50"
                borderRadius="md"
                openDelay={0}
                closeOnClick={true}
              >
                <Text fontSize="sm" color="gray.500" cursor="pointer">
                  ... {remainingCount} daha
                </Text>
              </Tooltip>
            )}
          </VStack>
        );
      },
    }),
    columnHelper.accessor("extras", {
      header: "Ekstralar",
      cell: ({ getValue }) => {
        const extras = getValue() || [];
        if (extras.length === 0) return "-";

        const displayCount = 1;
        const displayedExtras = extras.slice(0, displayCount);
        const remainingCount = extras.length - displayCount;

        return (
          <VStack align="start" spacing={1}>
            {displayedExtras.map((extra, index) => {
              const price = Number(extra.price) || 0;
              return (
              <Text key={index} fontSize="sm">
                  • {extra.name} (+{price.toFixed(2)}₺)
              </Text>
              );
            })}
            {remainingCount > 0 && (
              <Tooltip
                label={
                  <VStack align="start" spacing={1} p={2} maxH="200px" overflowY="auto" bg="white" borderRadius="md">
                    {extras.slice(displayCount).map((extra, index) => {
                      const price = Number(extra.price) || 0;
                      return (
                      <Text key={index} fontSize="sm" color="black">
                          • {extra.name} (+{price.toFixed(2)}₺)
                      </Text>
                      );
                    })}
                  </VStack>
                }
                hasArrow
                placement="bottom"
                bg="#e45858"
                borderRadius="md"
                openDelay={0}
                closeOnClick={true}
              >
                <Text fontSize="sm" color="gray.500" cursor="pointer">
                  ... {remainingCount} daha
                </Text>
              </Tooltip>
            )}
          </VStack>
        );
      },
    }),
    columnHelper.accessor("tags", {
      header: "Etiketler",
      cell: ({ getValue }) => {
        const tags = getValue() || [];
        if (tags.length === 0) return "-";
        
        return (
          <HStack spacing={1} flexWrap="wrap">
            {tags.slice(0, 3).map((tag, index) => {
              const displayTag = tag && tag.length > 0 
                ? tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase()
                : tag;
              return (
                <Badge
                  key={index}
                  colorScheme={
                    tag.toLowerCase() === 'vegan' || tag.toLowerCase() === 'vejetaryen' ? 'green' :
                    tag.toLowerCase() === 'glutensiz' ? 'blue' :
                    tag.toLowerCase() === 'halal' ? 'teal' :
                    tag.toLowerCase() === 'spicy' || tag.toLowerCase() === 'acılı' ? 'red' :
                    'gray'
                  }
                  fontSize="xs"
                  px={2}
                  py={1}
                  borderRadius="full"
                >
                  {displayTag}
                </Badge>
              );
            })}
            {tags.length > 3 && (
              <Tooltip
                label={
                  <VStack align="start" spacing={1} p={2}>
                    {tags.slice(3).map((tag, index) => {
                      const displayTag = tag && tag.length > 0 
                        ? tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase()
                        : tag;
                      return <Text key={index} fontSize="sm">{displayTag}</Text>;
                    })}
                  </VStack>
                }
                hasArrow
                placement="bottom"
              >
                <Badge colorScheme="gray" fontSize="xs" px={2} py={1} borderRadius="full" cursor="pointer">
                  +{tags.length - 3}
                </Badge>
              </Tooltip>
            )}
          </HStack>
        );
      },
    }),
    columnHelper.accessor("allergens", {
      header: "Alerjenler",
      cell: ({ getValue }) => {
        const allergens = getValue() || [];
        if (allergens.length === 0 || (allergens.length === 1 && allergens[0] === 'alerjen_yok')) {
          return <Badge colorScheme="green" fontSize="xs">Alerjen Yok</Badge>;
        }
        
        const filteredAllergens = allergens.filter(a => a !== 'alerjen_yok');
        if (filteredAllergens.length === 0) {
          return <Badge colorScheme="green" fontSize="xs">Alerjen Yok</Badge>;
        }
        
        return (
          <VStack align="start" spacing={1}>
            {filteredAllergens.slice(0, 2).map((allergen, index) => {
              const displayName = allergen
                .replaceAll('_', ' ')
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
              return (
                <Badge
                  key={index}
                  colorScheme="red"
                  fontSize="xs"
                  px={2}
                  py={1}
                  borderRadius="full"
                >
                  {displayName}
                </Badge>
              );
            })}
            {filteredAllergens.length > 2 && (
              <Tooltip
                label={
                  <VStack align="start" spacing={1} p={2}>
                    {filteredAllergens.slice(2).map((allergen, index) => {
                      const displayName = allergen
                        .replaceAll('_', ' ')
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');
                      return <Text key={index} fontSize="sm">{displayName}</Text>;
                    })}
                  </VStack>
                }
                hasArrow
                placement="bottom"
              >
                <Badge colorScheme="red" fontSize={{ base: "2xs", md: "xs" }} px={2} py={1} borderRadius="full" cursor="pointer">
                  +{filteredAllergens.length - 2}
                </Badge>
              </Tooltip>
            )}
          </VStack>
        );
      },
    }),
    columnHelper.accessor("nutritionInfo", {
      header: "Kalori",
      cell: ({ getValue }) => {
        const nutritionInfo = getValue();
        if (!nutritionInfo || !nutritionInfo.calories) return "-";
        
        return (
          <HStack spacing={1} align="center">
            <Text fontSize="sm" fontWeight="medium" color="orange.600">
              🔥 {nutritionInfo.calories} kcal
            </Text>
          </HStack>
        );
      },
    }),
    columnHelper.accessor("isFeatured", {
      header: "Öne Çıkan",
      cell: ({ row }) => (
        <Tooltip label={row.original.isFeatured ? "Öne çıkan ürün" : "Normal ürün"} placement="top">
          <Badge
            colorScheme={row.original.isFeatured ? "yellow" : "gray"}
            fontSize={{ base: "2xs", md: "xs" }}
            px={2}
            py={1}
            borderRadius="full"
          >
            {row.original.isFeatured ? "Öne Çıkan" : "Normal"}
          </Badge>
        </Tooltip>
      ),
    }),
    columnHelper.accessor("discount", {
      header: "İndirim",
      cell: ({ row }) => {
        const product = row.original;
        if (!product.isOnSale && !product.discountPrice) {
          return <Text color="gray.400" fontSize="sm">-</Text>;
        }
        
        return (
          <VStack align="start" spacing={1}>
            {product.isOnSale ? (
              <Badge colorScheme="green" fontSize={{ base: "2xs", md: "xs" }}>Aktif</Badge>
            ) : (
              <Badge colorScheme="gray" fontSize={{ base: "2xs", md: "xs" }}>Pasif</Badge>
            )}
            {product.discountStartDate && product.discountEndDate && (
              <Text fontSize="xs" color="gray.600">
                {new Date(product.discountStartDate).toLocaleDateString('tr-TR')} - {new Date(product.discountEndDate).toLocaleDateString('tr-TR')}
              </Text>
            )}
          </VStack>
        );
      },
    }),
    columnHelper.accessor("actions", {
      header: "İşlemler",
      cell: ({ row }) => (
        <HStack spacing={2} flexWrap="wrap">
          <Tooltip label="Düzenle" placement="top">
            <IconButton
              icon={<EditIcon />}
              colorScheme="blue"
              size={{ base: "xs", md: "sm" }}
              variant="outline"
              onClick={() => handleEditClick(row.original)}
              aria-label="Düzenle"
            />
          </Tooltip>
          <Tooltip label="İndirim Yönet" placement="top">
            <IconButton
              icon={<Icon as={MdLocalOffer} />}
              colorScheme="orange"
              size={{ base: "xs", md: "sm" }}
              variant="outline"
              onClick={() => handleDiscountClick(row.original)}
              aria-label="İndirim Yönet"
            />
          </Tooltip>
          <Tooltip label={row.original.isActive ? "Pasif Yap" : "Aktif Yap"} placement="top">
          <Switch
            isChecked={row.original.isActive}
            onChange={(e) => handleStatusChange(row.original._id, e.target.checked)}
            colorScheme="red"
            size={{ base: "sm", md: "md" }}
          />
          </Tooltip>
          <Tooltip label={row.original.isFeatured ? "Öne Çıkan Kaldır" : "Öne Çıkan Yap"} placement="top">
          <Switch
            isChecked={row.original.isFeatured}
            onChange={(e) => handleFeaturedChange(row.original._id, e.target.checked)}
            colorScheme="yellow"
            size={{ base: "sm", md: "md" }}
          />
          </Tooltip>
          <Tooltip label="Sil" placement="top">
          <Button
            variant="outline"
            colorScheme="red"
            size={{ base: "xs", md: "sm" }}
            onClick={() => handleDeleteClick(row.original._id)}
          >
            Sil
          </Button>
          </Tooltip>
        </HStack>
      ),
    }),
  ];

  // Arama filtresi uygula
  const filteredData = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return tableData;
    }
    const query = searchQuery.toLowerCase().trim();
    return tableData.filter((product) => {
      const nameMatch = product.name?.toLowerCase().includes(query);
      const categoryMatch = product.categoryName?.toLowerCase().includes(query);
      const tagMatch = product.tags?.some(tag => tag.toLowerCase().includes(query));
      const allergenMatch = product.allergens?.some(allergen => allergen.toLowerCase().includes(query));
      return nameMatch || categoryMatch || tagMatch || allergenMatch;
    });
  }, [tableData, searchQuery]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 5,
      },
    },
  });

  return (
    <Card>
      <Flex direction="column" gap={4}>
        {/* Arama Barı */}
        <Box>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Ürün ara (isim, kategori, tag, alerjen)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg="white"
              borderColor="gray.200"
              _hover={{ borderColor: "gray.300" }}
              _focus={{ borderColor: "#E45858", boxShadow: "0 0 0 1px #E45858" }}
              size={{ base: "sm", md: "md" }}
              fontSize={{ base: "sm", md: "md" }}
            />
          </InputGroup>
        </Box>
        <Flex justify="space-between" align="center" mb={4} flexDirection={{ base: "column", md: "row" }} gap={4}>
          <Flex align="center" gap={3} flexWrap="wrap">
            <Text fontWeight="bold" fontSize={{ base: "lg", md: "xl" }}>Ürün Tablosu</Text>
            {userPlan === 'STANDARD' && (
              <Badge colorScheme={tableData.length >= STANDARD_PRODUCT_LIMIT ? "red" : "yellow"} fontSize={{ base: "xs", md: "sm" }}>
                {tableData.length}/{STANDARD_PRODUCT_LIMIT} Ürün
              </Badge>
            )}
          </Flex>
          <HStack spacing={2} flexWrap="wrap">
          <Tooltip 
            label={userPlan === 'STANDARD' && tableData.length >= STANDARD_PRODUCT_LIMIT 
              ? `STANDARD plan için maksimum ${STANDARD_PRODUCT_LIMIT} ürün eklenebilir. PREMIUM plana yükseltin.`
              : "Yeni ürün ekle"}
            placement="left"
          >
            <Button
              leftIcon={<AddIcon />}
              colorScheme="red"
              onClick={handleOpenModal}
              size={{ base: "xs", md: "sm" }}
              isDisabled={userPlan === 'STANDARD' && tableData.length >= STANDARD_PRODUCT_LIMIT}
              opacity={userPlan === 'STANDARD' && tableData.length >= STANDARD_PRODUCT_LIMIT ? 0.6 : 1}
            >
              Yeni Ürün
            </Button>
          </Tooltip>
            <Button
              leftIcon={<Icon as={MdLocalOffer} />}
              colorScheme="orange"
              onClick={() => {
                if (tableData.length === 0) {
                  toast({
                    title: 'Uyarı',
                    description: 'Önce ürün eklemelisiniz',
                    status: 'warning',
                    duration: 3000,
                  });
                  return;
                }
                setIsDiscountModalOpen(true);
              }}
              size={{ base: "xs", md: "sm" }}
              variant="outline"
            >
              Ürün Bazlı İndirim
            </Button>
          </HStack>
        </Flex>

        <Box overflowX="auto">
          {tableData.length > 0 ? (
            <Table variant="simple" minW={{ base: "800px", md: "auto" }}>
              <Thead>
                <Tr>
                  {table.getHeaderGroups().map((headerGroup) => (
                    headerGroup.headers.map((header) => (
                      <Th 
                        key={header.id}
                        color="gray.600"
                        borderBottom="2px"
                        borderColor="gray.200"
                        py={{ base: 2, md: 4 }}
                        px={{ base: 2, md: 4 }}
                        fontSize={{ base: "xs", md: "sm" }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </Th>
                    ))
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {table.getRowModel().rows.map((row) => (
                  <Tr 
                    key={row.id}
                    _hover={{ bg: 'gray.50' }}
                    transition="background-color 0.2s"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Td 
                        key={cell.id}
                        borderColor="gray.100"
                        py={{ base: 2, md: 3 }}
                        px={{ base: 2, md: 4 }}
                        fontSize={{ base: "xs", md: "sm" }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : (
            <Flex 
              direction="column" 
              align="center" 
              justify="center" 
              p={8}
              bg="white"
              borderRadius="lg"
              border="1px dashed"
              borderColor="gray.200"
            >
              <Text color="gray.500" mb={4}>Henüz ürün eklenmemiş</Text>
              <Button
                leftIcon={<AddIcon />}
                colorScheme="red"
                variant="outline"
                size="sm"
                onClick={handleOpenModal}
              >
                İlk Ürünü Ekle
              </Button>
            </Flex>
          )}
        </Box>

        {/* Pagination */}
        {tableData.length > 0 && (
          <Flex w="100%" justify="space-between" align="center" px={{ base: 2, md: "25px" }} py="10px" borderTop="1px solid" borderColor="gray.200" flexDirection={{ base: "column", sm: "row" }} gap={2}>
            <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500" fontWeight="normal" textAlign={{ base: "center", sm: "left" }}>
              Toplam {tableData.length} ürün içinden {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} - {Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, tableData.length)} arası gösteriliyor
            </Text>
            <Flex gap={2} align="center">
              <IconButton
                onClick={() => table.previousPage()}
                isDisabled={!table.getCanPreviousPage()}
                icon={<ChevronLeftIcon />}
                size={{ base: "xs", md: "sm" }}
                variant="outline"
                aria-label="Önceki sayfa"
              />
              <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.500" fontWeight="normal">
                Sayfa {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}
              </Text>
              <IconButton
                onClick={() => table.nextPage()}
                isDisabled={!table.getCanNextPage()}
                icon={<ChevronRightIcon />}
                size={{ base: "xs", md: "sm" }}
                variant="outline"
                aria-label="Sonraki sayfa"
              />
            </Flex>
          </Flex>
        )}
      </Flex>

      <AddProductModal
        isOpen={isOpen}
        onClose={handleCloseModal}
        onAdd={handleAddProduct}
        categories={categories}
        products={tableData}
        editingProduct={editingProduct}
      />

      <AddDiscountProductModal
        isOpen={isDiscountModalOpen}
        onClose={handleCloseDiscountModal}
        onSave={handleDiscountUpdate}
        product={productForDiscount}
        products={tableData}
      />

      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Ürünü Sil
            </AlertDialogHeader>

            <AlertDialogBody>
              Bu ürünü silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteDialogClose}>
                İptal
              </Button>
              <Button colorScheme="red" onClick={handleDeleteProduct} ml={3}>
                Sil
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      
      <PremiumUpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        onConfirm={async () => {
          // Plan bilgisini güncelle
          const pricingInfo = await pricingService.getPricingInfo();
          if (pricingInfo?.success && pricingInfo?.data?.business?.plan) {
            setUserPlan(pricingInfo.data.business.plan);
          }
          // Ürün listesini yenile
          await onRefresh();
        }}
      />
    </Card>
  );
}
